import * as yup from 'yup';
import { startOfDay, subDays } from 'date-fns/fp';
import { parseISO } from 'date-fns';
import {
  validator,
  requiredFormObject,
  requiredFormString,
  inputShape,
} from 'lib/validator';
import { makeAPIBodyErrorProcessor } from 'lib/errorHelpers';
import { pipe } from 'lib/funcHelpers';
import { API_ERROR_CODES } from 'config/errorCodes';
import { isCustomAqlLevel } from 'config/inspectionConfig';

const customSampleQtySchema = yup
  .number()
  .transform((value, original) =>
    original === '' || original === null ? undefined : value,
  )
  .typeError('Must be a number')
  .required('Sample size is required')
  .moreThan(0, 'Must be greater than 0');

const inspectionsFormValidationSchema = yup.object().shape({
  linkedResource: requiredFormObject(),
  source: yup.object().when('linkedResource', {
    is: (res) => res.value?.resourceType === 'asset',
    then: requiredFormObject(),
  }),
  date: inputShape(
    yup
      .string()
      .nullable()
      .test(
        'inspectionDate',
        'Date cannot be in the past',
        (val) =>
          !val || parseISO(val) >= pipe(startOfDay, subDays(2))(new Date()),
      ),
  ),
  inspectionWindow: inputShape(
    yup
      .array()
      .test(
        'inspectionWindow',
        'Invalid date range',
        (val) => !!val[0] === !!val[1],
      ),
  ),
  status: requiredFormString(),
  type: requiredFormObject(),
  plan: inputShape(yup.object().nullable()),
  majorDefect: yup.object().when('aqlLevel', {
    is: (level) => level?.value !== null && level?.value !== undefined,
    then: requiredFormObject(),
  }),
  minorDefect: yup.object().when('aqlLevel', {
    is: (level) => level?.value !== null && level?.value !== undefined,
    then: requiredFormObject(),
  }),
  functionalDefect: yup.object().when('aqlLevel', {
    is: (level) => level?.value !== null && level?.value !== undefined,
    then: requiredFormObject(),
  }),
  criticalDefectValue: yup.object().when('criticalDefectRule', {
    is: (rule) => rule?.value === '%',
    then: inputShape(
      yup
        .number()
        .typeError('Must be a number')
        .max(100, 'Cannot exceed 100%'),
    ),
  }),
  customSampleQty: yup.object().when(['aqlLevel', 'customSampleRule'], {
    is: (level, rule) => isCustomAqlLevel(level) && rule?.value === '%',
    then: inputShape(customSampleQtySchema.max(100, 'Cannot exceed 100%')),
    otherwise: yup.object().when('aqlLevel', {
      is: isCustomAqlLevel,
      then: inputShape(customSampleQtySchema),
    }),
  }),
});

const apiPathToFormPath = (path) =>
  path
    .replace('inspectionDate', 'date')
    .replace('inspectionType', 'type')
    .replace('inspectionPlan', 'plan')
    .replace(/locationSource\..*/, 'source');

const inspectionErrors = {
  ...API_ERROR_CODES,
  'inspections.planAndTypeDoesNotMatch':
    'Inspection plan does not match selected inspection type',
  'inspections.quantityAboveLot':
    'Quantity cannot be more than the lot quantity',
};

export const processInspectionFormAPIError = makeAPIBodyErrorProcessor({
  transformPath: apiPathToFormPath,
  errorCodes: inspectionErrors,
});

const inspectionsFormValidator = (state) =>
  validator(state, inspectionsFormValidationSchema);

export default inspectionsFormValidator;
